// TurfAI Connection Test Utility

import { dmsService, workflowService, initializeTurfAI } from './index';
import { workflowResolver } from './workflowResolver';

export async function testTurfAIConnection(): Promise<{
  success: boolean;
  results: {
    auth: boolean;
    dms: boolean;
    workflow: boolean;
  };
  error?: string;
}> {
  const results = {
    auth: false,
    dms: false,
    workflow: false,
  };

  try {
    // Step 0: Log configuration
    console.log('🔧 TurfAI Configuration:');
    console.log('DMS URL:', import.meta.env.VITE_TURFAI_DMS_URL);
    console.log('API URL:', import.meta.env.VITE_TURFAI_API_URL);
    console.log('Test Email:', import.meta.env.VITE_TURFAI_TEST_EMAIL);
    
    // Step 1: Test authentication
    console.log('🔐 Testing TurfAI authentication...');
    const authSuccess = await initializeTurfAI();
    results.auth = authSuccess;
    
    if (!authSuccess) {
      throw new Error('Authentication failed');
    }
    console.log('✓ Authentication successful');

    // Step 2: Test DMS connection
    console.log('Testing DMS connection...');
    const dmsConnected = await dmsService.checkConnection();
    results.dms = dmsConnected;
    
    if (!dmsConnected) {
      throw new Error('DMS connection failed');
    }
    console.log('✓ DMS connection successful');

    // Step 3: Test Workflow service connection
    console.log('Testing Workflow service connection...');
    const workflowConnected = await workflowService.checkConnection();
    results.workflow = workflowConnected;
    
    if (!workflowConnected) {
      throw new Error('Workflow service connection failed');
    }
    console.log('✓ Workflow service connection successful');

    console.log('All TurfAI services connected successfully!');
    return { success: true, results };

  } catch (error) {
    console.error('TurfAI connection test failed:', error);
    return { 
      success: false, 
      results,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Test function for document types
export async function testDocumentTypes(): Promise<void> {
  try {
    await initializeTurfAI();
    
    console.log('Fetching document types...');
    const documentTypes = await dmsService.getRequiredDocumentTypes();
    
    console.log('Available document types:');
    documentTypes.forEach(dt => {
      console.log(`- ${dt.attributes.name}: ${dt.attributes.description}`);
    });
  } catch (error) {
    console.error('Failed to fetch document types:', error);
  }
}

// Test function for creating a collection
export async function testCreateCollection(): Promise<string | null> {
  try {
    await initializeTurfAI();
    
    const testEmployeeData = {
      employee_id: `TEST_${Date.now()}`,
      name: 'Test Employee',
      department: 'Engineering',
      start_date: new Date().toISOString(),
      joiner_type: 'employee' as const,
      experience_level: 'experienced' as const,
      email: 'test@example.com'
    };
    
    console.log('Creating test collection...');
    const collection = await dmsService.createOnboardingCollection(testEmployeeData);
    
    console.log('Collection created successfully:');
    console.log(`- ID: ${collection.id}`);
    console.log(`- Name: ${collection.attributes.name}`);
    
    return collection.id;
  } catch (error) {
    console.error('Failed to create collection:', error);
    return null;
  }
}

// Test function for workflow resolution
export async function testWorkflowResolution(): Promise<void> {
  try {
    await initializeTurfAI();
    
    console.log('🔄 Testing workflow resolution...');
    
    // Test resolving available workflows
    const testWorkflows = [
      'sample-document-processing',
      'extract-and-save',
      'document-classification',
      'data-extraction'
    ];
    
    for (const workflowName of testWorkflows) {
      try {
        console.log(`🔍 Resolving workflow: ${workflowName}`);
        const resolved = await workflowResolver.resolveWorkflow(workflowName);
        console.log(`✅ Resolved '${workflowName}' to ID: ${resolved.id}`);
        console.log(`   Activity: ${resolved.activity?.name || 'N/A'}`);
        console.log(`   Type: ${resolved.definition.type}`);
      } catch (error) {
        console.log(`❌ Failed to resolve '${workflowName}': ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    
    console.log('🎉 Workflow resolution test completed!');
  } catch (error) {
    console.error('Workflow resolution test failed:', error);
  }
}